import React, { useState, useRef } from "react";
import { Handle, Position } from "reactflow";
import { getKnowledgeNodeStyle } from "../utils/nodePresentation.js";
import { normalizeNodeImportance, normalizeNodeResources } from "../utils/nodeContent.js";
import NodeIcon from "./NodeIcon.jsx";
import PublicNodeDescriptionTooltip from "./PublicNodeDescriptionTooltip.jsx";
import { Map as MapIcon, LoaderCircle } from "lucide-react";
import { fetchPublicLinkStatuses } from "../utils/publicGallery.js";
import { API_BASE } from "../api/base.js";
import { showErrorToast } from "../utils/toast.js";

export default function PublicNode({ data, id }) {
  const [openingLink, setOpeningLink] = useState(false);
  const nodeRef = useRef(null);
  const importance = normalizeNodeImportance(data.importance, data.is_core);
  const nodePresentation = getKnowledgeNodeStyle(importance, data.hierarchy_emphasis, data.theme_id);
  const iconSize = Math.max(12, Math.round(nodePresentation.fontSize + 2));
  const resources = normalizeNodeResources(data.resources);
  const hasDetails = Boolean(data.description) || resources.length > 0;
  const showDescription = hasDetails && Boolean(data.tooltipOpen);
  const setDetailsOpen = (open) => {
    if (data.onDetailsVisibilityChange) data.onDetailsVisibilityChange(Boolean(open));
    else data.onToggleDetails?.();
  };
  const tooltipId = `node-description-${id}`;
  const targetMapId = data.semantic_data?.map_link?.target_map_id;
  const linkAvailability = !targetMapId
    ? "none"
    : data.mapLinkContext?.publicState === "public_allowed"
      ? "public"
      : data.mapLinkContext?.publicState === "checking"
        ? "checking"
        : "unavailable";
  const linkApiBase = String(data.mapLinkContext?.apiBase || API_BASE).replace(/\/+$/, "");
  const linkAppBase = String(data.mapLinkContext?.appBase || "").replace(/\/+$/, "");
  const publicViewerBase = String(data.mapLinkContext?.publicViewerBase || "").replace(/\/+$/, "");
  const embedded = Boolean(data.mapLinkContext?.embedded);
  const appRoute = (route) => linkAppBase ? `${linkAppBase}${route.startsWith("/") ? route : `/${route}`}` : route;
  const openLinkedMap = async (event) => {
    event.stopPropagation();
    if (!targetMapId || openingLink || linkAvailability !== "public") return;
    const linkedWindow = embedded ? window.open("about:blank", "_blank") : null;
    if (linkedWindow) linkedWindow.opener = null;
    setOpeningLink(true);
    try {
      let result, ok;
      if (data.mapLinkContext?.publicStatusBase) {
        [result] = await fetchPublicLinkStatuses([targetMapId], data.mapLinkContext.publicStatusBase);
        ok = result.state === "public_allowed";
      } else {
        const response = await fetch(`${linkApiBase}/map-links/${targetMapId}/resolve?public_context=true`, { credentials: "omit", cache: "no-store" });
        result = await response.json().catch(() => ({}));
        ok = response.ok;
      }
      const destination = ok && result.route && result.state === "public_allowed"
        ? (publicViewerBase ? `${publicViewerBase}/view/${encodeURIComponent(targetMapId)}` : appRoute(result.route))
        : null;
      if (destination && embedded && linkedWindow) linkedWindow.location.replace(destination);
      else if (destination && embedded) showErrorToast("Allow pop-ups to open this linked map.");
      else if (destination) window.location.assign(destination);
      else { linkedWindow?.close(); showErrorToast("This linked map is unavailable."); }
    } catch { linkedWindow?.close(); showErrorToast("This linked map could not be opened."); }
    finally { setOpeningLink(false); }
  };

  return (
    <>
      <div
        ref={nodeRef}
        className="knowledge-map-node__surface"
        data-knowledge-node-id={String(id)}
        tabIndex={hasDetails ? 0 : undefined}
        aria-describedby={showDescription ? tooltipId : undefined}
        aria-expanded={hasDetails ? showDescription : undefined}
        role={hasDetails ? "button" : undefined}
        onClick={(event) => {
          if (!hasDetails) return;
          event.stopPropagation();
          setDetailsOpen(!showDescription);
        }}
        onKeyDown={(event) => {
          if (!hasDetails) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setDetailsOpen(!showDescription);
          }
          if (event.key === "Escape" && showDescription) setDetailsOpen(false);
        }}
        style={{
          ...nodePresentation,
          "--knowledge-node-font-size": `${nodePresentation.fontSize}px`,
          "--knowledge-node-font-weight": nodePresentation.fontWeight,
          "--knowledge-node-line-height": nodePresentation.lineHeight,
          position: "relative",
          cursor: hasDetails ? "pointer" : "default",
          opacity: Number.isFinite(Number(data.contextualOpacity)) ? Number(data.contextualOpacity) : 1,
          transition: data.disableOpacityTransition ? "none" : "opacity 160ms ease",
          outline: showDescription ? "2px solid var(--map-core-border, #c58a27)" : "none",
          outlineOffset: showDescription ? 3 : 0,
        }}
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={false}
          aria-hidden="true"
          style={{ opacity: 0, pointerEvents: "none" }}
        />
        <span className="knowledge-map-node__label" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <NodeIcon name={data.icon} size={iconSize} />
          <span className="knowledge-map-node__label-text">{data.label}</span>
        </span>
        {resources.length > 0 && (
          <span aria-hidden="true" style={{ position: "absolute", right: -6, top: -6, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: "var(--map-node)", border: "1px solid var(--map-node-border)", fontSize: 9, lineHeight: "14px" }}>
            {resources.length}
          </span>
        )}
        {targetMapId && <button type="button" onClick={openLinkedMap} onPointerDown={(event) => event.stopPropagation()} className="knowledge-map-node__map-link" aria-label={linkAvailability === "unavailable" ? "Linked map is private or unavailable" : "Open linked public map in a new tab"} title={linkAvailability === "checking" ? "Checking linked map availability" : linkAvailability === "unavailable" ? "Linked map is private or unavailable" : "Open linked public map"} disabled={linkAvailability !== "public" || openingLink} style={{ position: "absolute", right: -9, bottom: -9, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 999, border: "1px solid var(--map-node-border)", background: "var(--map-node)", color: "var(--map-node-text)", cursor: linkAvailability === "public" ? "pointer" : "not-allowed", opacity: linkAvailability === "public" ? 1 : 0.42, filter: linkAvailability === "public" ? "none" : "grayscale(1)" }}>{openingLink || linkAvailability === "checking" ? <LoaderCircle size={12} className="animate-spin" /> : <MapIcon size={13} strokeWidth={2} />}</button>}
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          aria-hidden="true"
          style={{ opacity: 0, pointerEvents: "none" }}
        />
      </div>

      <PublicNodeDescriptionTooltip
        id={id}
        open={showDescription}
        anchorElement={() => nodeRef.current?.closest(".react-flow__node") || nodeRef.current}
        description={data.description}
        resources={resources}
      />
    </>
  );
}
